/**
 * InvoiceDetail page — full invoice data, line items, source email metadata,
 * linked vendor/contract/sites, and lifecycle status badge.
 *
 * Requirements: 44.9, 44.10, 44.11, 44.26, 44.29, 44.30, 44.32, 44.33
 */

import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { invoiceService } from '@/services/invoiceService';
import { StatusBadge, type BadgeVariant } from '@/components/ui/StatusBadge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';

const LIFECYCLE_STATUS_VARIANTS: Record<string, BadgeVariant> = {
  'Pending Review': 'yellow',
  'Pending VP Approval': 'blue',
  Approved: 'green',
  Paid: 'green',
  Rejected: 'red',
  Disputed: 'red',
};

export function InvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const invoiceId = Number(id);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => invoiceService.getById(invoiceId),
    enabled: !isNaN(invoiceId),
  });

  if (isNaN(invoiceId)) {
    return <ErrorMessage message="Invalid invoice ID." />;
  }

  if (isLoading) {
    return <LoadingSpinner message="Loading invoice details…" />;
  }

  if (error || !data) {
    return (
      <ErrorMessage
        message={(error as Error)?.message ?? 'Failed to load invoice.'}
        onRetry={() => refetch()}
      />
    );
  }

  const { invoice, lineItems } = data;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
            <Link to="/invoices" style={{ color: '#64748b', textDecoration: 'none', fontSize: '0.8rem' }}>
              ← Invoices
            </Link>
          </div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>
            Invoice {invoice.invoiceNumber ?? `#${invoice.invoiceId}`}
          </h1>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', alignItems: 'center' }}>
            <StatusBadge
              label={invoice.lifecycleStatus}
              variant={LIFECYCLE_STATUS_VARIANTS[invoice.lifecycleStatus] ?? 'gray'}
            />
            <StatusBadge label={invoice.departmentScope} variant="blue" />
            {invoice.isDuplicate && <StatusBadge label="Duplicate" variant="red" />}
          </div>
        </div>
      </div>

      {/* Invoice Info */}
      <section style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, marginTop: 0, marginBottom: '0.75rem' }}>Invoice Details</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem', fontSize: '0.875rem' }}>
          <div>
            <span style={{ color: '#64748b' }}>Vendor: </span>
            {invoice.vendorId ? (
              <Link to={`/vendors/${invoice.vendorId}`} style={{ color: '#c8102e', textDecoration: 'none' }}>
                {invoice.vendorName ?? `Vendor #${invoice.vendorId}`}
              </Link>
            ) : (
              invoice.vendorName ?? '—'
            )}
          </div>
          <div>
            <span style={{ color: '#64748b' }}>Contract: </span>
            {invoice.contractId ? (
              <Link to={`/contracts/${invoice.contractId}`} style={{ color: '#c8102e', textDecoration: 'none' }}>
                Contract #{invoice.contractId}
              </Link>
            ) : '—'}
          </div>
          <div><span style={{ color: '#64748b' }}>Invoice Date:</span> {invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString() : '—'}</div>
          <div><span style={{ color: '#64748b' }}>Due Date:</span> {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : '—'}</div>
          <div><span style={{ color: '#64748b' }}>Subtotal:</span> {invoice.subtotal != null ? `$${Number(invoice.subtotal).toLocaleString()}` : '—'}</div>
          <div><span style={{ color: '#64748b' }}>Tax:</span> {invoice.tax != null ? `$${Number(invoice.tax).toLocaleString()}` : '—'}</div>
          <div><span style={{ color: '#64748b' }}>Total Amount:</span> {invoice.totalAmount != null ? `$${Number(invoice.totalAmount).toLocaleString()}` : '—'}</div>
          <div><span style={{ color: '#64748b' }}>Payment Terms:</span> {invoice.paymentTerms ?? '—'}</div>
          <div><span style={{ color: '#64748b' }}>PO Number:</span> {invoice.poNumber ?? '—'}</div>
          <div><span style={{ color: '#64748b' }}>Source:</span> {invoice.sourceType ?? '—'}</div>
          <div><span style={{ color: '#64748b' }}>Created:</span> {new Date(invoice.createdAt).toLocaleDateString()}</div>
          <div><span style={{ color: '#64748b' }}>Updated:</span> {new Date(invoice.updatedAt).toLocaleDateString()}</div>
        </div>
      </section>

      {/* Coding & Approval Info */}
      <section style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, marginTop: 0, marginBottom: '0.75rem' }}>Coding & Approval</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem', fontSize: '0.875rem' }}>
          <div>
            <span style={{ color: '#64748b' }}>Coding: </span>
            {invoice.codingSummary ? (
              <span style={{ display: 'inline-flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                {invoice.codingSummary.split(', ').map((pair, i) => {
                  const [branch] = pair.split('-');
                  return (
                    <Link
                      key={i}
                      to={`/sites/${branch}`}
                      style={{
                        display: 'inline-block',
                        padding: '0.1rem 0.4rem',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        fontFamily: 'monospace',
                        background: '#f1f5f9',
                        border: '1px solid #e2e8f0',
                        color: '#c8102e',
                        textDecoration: 'none',
                      }}
                    >
                      {pair}
                    </Link>
                  );
                })}
              </span>
            ) : '—'}
          </div>
          <div><span style={{ color: '#64748b' }}>Coded By:</span> {invoice.codedBy ?? '—'}</div>
          <div><span style={{ color: '#64748b' }}>Coded At:</span> {invoice.codedAt ? new Date(invoice.codedAt).toLocaleString() : '—'}</div>
          <div><span style={{ color: '#64748b' }}>Approved By:</span> {invoice.approvedBy ?? '—'}</div>
          <div><span style={{ color: '#64748b' }}>Approved At:</span> {invoice.approvedAt ? new Date(invoice.approvedAt).toLocaleString() : '—'}</div>
        </div>
        {invoice.rejectionNotes && (
          <div style={{ marginTop: '0.75rem', fontSize: '0.875rem' }}>
            <span style={{ color: '#dc2626', fontWeight: 500 }}>Rejection Notes:</span>
            <p style={{ margin: '0.25rem 0 0', color: '#334155' }}>{invoice.rejectionNotes}</p>
          </div>
        )}
      </section>

      {/* Source Email Metadata */}
      {invoice.sourceType === 'Email' && (
        <section style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginTop: 0, marginBottom: '0.75rem' }}>Source Email</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem', fontSize: '0.875rem' }}>
            <div><span style={{ color: '#64748b' }}>Mailbox:</span> {invoice.sourceMailbox ?? '—'}</div>
            <div><span style={{ color: '#64748b' }}>Subject:</span> {invoice.sourceEmailSubject ?? '—'}</div>
            <div><span style={{ color: '#64748b' }}>Sender:</span> {invoice.sourceEmailSender ?? '—'}</div>
            <div><span style={{ color: '#64748b' }}>Date:</span> {invoice.sourceEmailDate ? new Date(invoice.sourceEmailDate).toLocaleString() : '—'}</div>
            <div><span style={{ color: '#64748b' }}>Message ID:</span> <span style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>{invoice.sourceEmailMessageId ?? '—'}</span></div>
            <div><span style={{ color: '#64748b' }}>Attachments:</span> {invoice.sourceEmailAttachmentCount ?? '—'}</div>
          </div>
        </section>
      )}

      {/* Document */}
      {invoice.egnyteFilePath && (
        <section style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginTop: 0, marginBottom: '0.75rem' }}>Document</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.875rem' }}>
            {/* K: drive path with copy button */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ color: '#64748b' }}>Local Path: </span>
              <code style={{ fontSize: '0.8rem', background: '#f1f5f9', padding: '0.2rem 0.5rem', borderRadius: '4px', wordBreak: 'break-all' }}>
                K:{invoice.egnyteFilePath.replace('/Shared/Baker_Distributing', '').replace(/\//g, '\\')}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`K:${(invoice.egnyteFilePath ?? '').replace('/Shared/Baker_Distributing', '').replace(/\//g, '\\')}`);
                }}
                style={{ padding: '0.2rem 0.5rem', fontSize: '0.7rem', border: '1px solid #e2e8f0', borderRadius: '4px', background: '#fff', cursor: 'pointer', color: '#64748b' }}
                title="Copy path to clipboard"
              >
                Copy
              </button>
            </div>
            {/* Egnyte web link */}
            {invoice.egnyteFileId && (
              <div>
                <span style={{ color: '#64748b' }}>Web: </span>
                <a
                  href={`https://bakerdist.egnyte.com/navigate/file/${invoice.egnyteFileId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#c8102e', textDecoration: 'none' }}
                >
                  Open in Egnyte
                  <svg style={{ display: 'inline', width: '12px', height: '12px', marginLeft: '4px', verticalAlign: 'middle' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Line Items */}
      <section style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, marginTop: 0, marginBottom: '0.75rem' }}>
          Line Items ({lineItems.length})
        </h2>
        {lineItems.length === 0 ? (
          <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>No line items for this invoice.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', minWidth: '700px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>Description</th>
                  <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: '#475569' }}>Qty</th>
                  <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: '#475569' }}>Unit Price</th>
                  <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: '#475569' }}>Amount</th>
                  <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>GL Code</th>
                  <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>Cost Center</th>
                  <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>Category</th>
                  <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>Site</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li) => (
                  <tr key={li.lineItemId} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '0.4rem 0.5rem' }}>
                      {li.description ?? '—'}
                      {li.aiSuggestedGLCode && li.aiSuggestedGLCode !== li.glCode && (
                        <div style={{ fontSize: '0.7rem', color: '#8b5cf6' }}>
                          AI suggested: {li.aiSuggestedGLCode}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>{li.quantity ?? '—'}</td>
                    <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>
                      {li.unitPrice != null ? `$${Number(li.unitPrice).toLocaleString()}` : '—'}
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>
                      {li.amount != null ? `$${Number(li.amount).toLocaleString()}` : '—'}
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>{li.glCode ?? '—'}</td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>{li.costCenter ?? '—'}</td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>{li.budgetCategory ?? '—'}</td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>
                      {li.siteNumber ? (
                        <Link to={`/sites/${li.siteNumber}`} style={{ color: '#c8102e', textDecoration: 'none' }}>
                          {li.siteNumber}
                        </Link>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
